// POSProductsTable.js - Product table view for POS cashier
// Displays all products with: Image, Name, Price, Stock, and Quick Add to Cart button
import React, { useState, useMemo, useEffect } from "react";
import EmptyState from "../../CommonComponents/EmptyState";
import SearchInput from "../../CommonComponents/SearchInput";
import ReactSelectField from "../../CommonComponents/ReactSelectField";
import { ArrowDown, ArrowUp, ArrowUpDown, CheckCircle2, Package, PackageSearch, ScanLine, TriangleAlert, WifiOff, XCircle } from "lucide-react";
import { usePOSProductsContext } from "../contexts/POSProductContext";
import ProductImageFallback from "../../CommonComponents/ProductImageFallback";
import ProductImagePreviewModal from "../../admin/products/ProductImagePreviewModal";
import { PRODUCT_CATEGORY_FILTER_OPTIONS } from "../../../constants/productCategories";
import { getBarcodeValidation } from "../../../utils/barcodeUtils";

// Utility function to format price in PHP currency
const formatPrice = (amount) => {
    if (isNaN(amount)) return "₱0.00";
    return new Intl.NumberFormat("en-PH", {
        style: "currency",
        currency: "PHP",
        minimumFractionDigits: 2,
    }).format(amount);
};

// Helper component for product image
const ProductImage = ({ imageUrl, name, outOfStock, onPreview }) => {
    const [src, setSrc] = React.useState(imageUrl || null);

    const handleImageError = (error) => {
        console.warn(`[ProductImage] Failed to load image for "${name}": ${imageUrl}`, error);
        setSrc(null);
    };

    return (
        <button
            type="button"
            onClick={onPreview}
            className="relative h-11 w-11 flex-shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition hover:scale-[1.03] hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:ring-offset-2"
            aria-label={`Preview ${name} image`}
        >
            {src ? (
                <img
                    src={src}
                    alt={name}
                    className="h-full w-full object-cover"
                    onError={handleImageError}
                />
            ) : (
                <ProductImageFallback className="h-full w-full rounded-xl" aria-label="No product image" />
            )}
            {outOfStock && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/45 pointer-events-none">
                    <span className="text-white text-[9px] font-bold">Out</span>
                </div>
            )}
        </button>
    );
};

function POSProductsTable() {
    // Get products from context
    const { products, loading, error, fetchProducts } = usePOSProductsContext();

    // Local state
    const [searchTerm, setSearchTerm] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("All");
    const [stockSort, setStockSort] = useState("normal");
    const [currentPage, setCurrentPage] = useState(1);
    const [previewProduct, setPreviewProduct] = useState(null);
    const productsPerPage = 10;

    // Filter products by search
    const filteredProducts = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();

        return products.filter((p) => {
            const productCategory = p.category || "Others";
            const matchesSearch = !normalizedSearch
                || p.name.toLowerCase().includes(normalizedSearch)
                || String(p.product_code || "").toLowerCase().includes(normalizedSearch)
                || productCategory.toLowerCase().includes(normalizedSearch);
            const matchesCategory = categoryFilter === "All" || productCategory === categoryFilter;

            return matchesSearch && matchesCategory;
        });
    }, [products, searchTerm, categoryFilter]);
    const productSuggestions = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();
        if (!normalizedSearch) return [];
        const uniqueNames = new Set();

        return products
            .map((product) => product.name)
            .filter((name) => {
                if (!name || uniqueNames.has(name)) return false;
                const matches = name.toLowerCase().includes(normalizedSearch);
                if (matches) {
                    uniqueNames.add(name);
                }
                return matches;
            })
            .slice(0, 6);
    }, [products, searchTerm]);

    const sortedProducts = useMemo(() => {
        if (stockSort === "normal") return filteredProducts;

        return [...filteredProducts].sort((a, b) => {
            const difference = (Number(a.stock) || 0) - (Number(b.stock) || 0);
            return stockSort === "low" ? difference : -difference;
        });
    }, [filteredProducts, stockSort]);

    const nextStockSort = stockSort === "normal" ? "low" : stockSort === "low" ? "high" : "normal";
    const stockSortLabels = { normal: "Normal", low: "Low to high", high: "High to low" };
    const StockSortIcon = stockSort === "normal" ? ArrowUpDown : stockSort === "low" ? ArrowUp : ArrowDown;

    // Sort the full filtered list before pagination.
    const totalPages = Math.max(Math.ceil(filteredProducts.length / productsPerPage), 1);
    const currentProducts = sortedProducts.slice(
        (currentPage - 1) * productsPerPage,
        currentPage * productsPerPage
    );

    const selectedCategoryOption = PRODUCT_CATEGORY_FILTER_OPTIONS.find((option) => option.value === categoryFilter)
        || PRODUCT_CATEGORY_FILTER_OPTIONS[0];

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, categoryFilter]);

    return (
        <div className="flex w-full max-w-full min-w-0 flex-col gap-4 pt-3">
            <section className="relative z-30 rounded-[30px] border border-slate-200 bg-white p-6 shadow-[0_24px_80px_rgba(15,23,42,0.08)]">
                <div className="grid gap-5 xl:grid-cols-[minmax(260px,1fr),minmax(0,1.35fr)] xl:items-end">
                    <div className="min-w-0">
                        <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
                            <PackageSearch className="h-4 w-4" />
                            Products Catalog
                        </div>
                        <h1 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950">Products List</h1>
                        <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
                            Browse branch inventory, prices, warehouse stock, and availability.
                        </p>
                    </div>
                    <div className="grid min-w-0 gap-3 md:grid-cols-[minmax(210px,240px),minmax(280px,1fr)]">
                        <ReactSelectField
                            options={PRODUCT_CATEGORY_FILTER_OPTIONS}
                            value={selectedCategoryOption}
                            onChange={(option) => setCategoryFilter(option?.value || "All")}
                            placeholder="All Categories"
                            isSearchable
                            ariaLabel="Filter cashier products by category"
                        />
                        <div className="min-w-0">
                            <SearchInput
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(1);
                                }}
                                onSuggestionSelect={(productName) => {
                                    setSearchTerm(productName);
                                    setCurrentPage(1);
                                }}
                                suggestions={productSuggestions}
                                placeholder="Search by product name or barcode..."
                                className="group relative w-full"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Table Container */}
            {error ? (
                <EmptyState
                    icon={WifiOff}
                    title="Failed to Load Products"
                    message="Check your internet connection and try again."
                    onRetry={fetchProducts}
                    retryLabel="Reload"
                />
            ) : loading ? (
                <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500 shadow-sm">Loading products...</div>
            ) : !filteredProducts.length ? (
                <EmptyState
                    icon={Package}
                    title={searchTerm ? "No Products Found" : "No Products Available"}
                    message={searchTerm ? "Try a different search term." : "No products are currently available."}
                />
            ) : (
                <div className="relative z-0 overflow-x-auto rounded-[28px] border border-slate-200/80 bg-white shadow-[0_18px_45px_rgba(15,23,42,0.06)]">
                    <table className="min-w-[1180px] w-full border-collapse lg:min-w-[1040px] xl:min-w-0">
                        <thead
                            className="border-b"
                            style={{
                                background: "linear-gradient(180deg, var(--color-primary), color-mix(in srgb, var(--color-primary) 82%, black))",
                                borderColor: "color-mix(in srgb, var(--color-primary) 72%, black)",
                            }}
                        >
                            <tr>
                                <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Item</th>
                                <th className="px-4 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Category</th>
                                <th className="px-4 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Barcode</th>
                                <th className="px-4 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Description</th>
                                <th className="px-4 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Universal Price</th>
                                <th
                                    scope="col"
                                    aria-sort={stockSort === "normal" ? "none" : stockSort === "low" ? "ascending" : "descending"}
                                    className="px-4 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5"
                                >
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setStockSort(nextStockSort);
                                            setCurrentPage(1);
                                        }}
                                        title={`Branch Stock: ${stockSortLabels[stockSort]}. Click for ${stockSortLabels[nextStockSort].toLowerCase()}.`}
                                        aria-label={`Branch Stock: ${stockSortLabels[stockSort]}. Sort ${stockSortLabels[nextStockSort].toLowerCase()}`}
                                        className="inline-flex items-center rounded-lg text-white uppercase tracking-[0.18em] transition hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                                    >
                                        <span className="inline-flex items-center gap-1.5 text-white">
                                            Branch Stock
                                            <StockSortIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                                        </span>
                                    </button>
                                </th>
                                <th className="px-4 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Warehouse Stock</th>
                                <th className="px-4 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentProducts.map((product) => {
                                const isOutOfStock = product.stock === 0 || product.stock === null || product.stock === undefined;
                                const warehouseStock = product.warehouse_stock || 0;
                                const hasWarehouseStock = warehouseStock > 0;
                                const isLowWarehouseStock = warehouseStock > 0 && warehouseStock < 10;

                                return (
                                    <tr
                                        key={product.id}
                                        className="border-b border-slate-100 bg-slate-50/85 transition hover:bg-emerald-50/70 even:bg-white last:border-0 sm:bg-transparent sm:hover:bg-slate-50/70 sm:even:bg-transparent"
                                    >
                                        <td className="px-4 py-2.5 xl:px-5">
                                            <div className="flex items-center gap-2.5">
                                                <ProductImage
                                                    imageUrl={product.image}
                                                    name={product.name}
                                                    outOfStock={isOutOfStock}
                                                    onPreview={() => setPreviewProduct(product)}
                                                />
                                                <div className="min-w-0">
                                                    <p className="truncate text-[13px] font-semibold leading-5 text-slate-900">{product.name}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-2.5 text-center xl:px-5">
                                            <span className="inline-flex min-w-[112px] items-center justify-center rounded-full border border-indigo-100 bg-indigo-50 px-2.5 py-0.5 text-xs font-semibold text-indigo-700 shadow-sm">
                                                {product.category || "Others"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2.5 text-center xl:px-5">
                                            <span className="inline-flex min-w-[148px] items-center justify-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs font-medium text-slate-600 shadow-sm whitespace-nowrap lg:min-w-[112px]">
                                                <ScanLine className="h-3.5 w-3.5" />
                                                {product.product_code || '-'}
                                            </span>
                                            <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                                                {product.barcode_type === "INTERNAL" || !getBarcodeValidation(product.product_code).isValidGs1Format ? "Internal" : "Manufacturer"}
                                            </p>
                                        </td>
                                        <td className="px-4 py-2.5 text-center text-xs text-slate-600 xl:px-5">
                                            <span className="line-clamp-2">{product.description && product.description !== product.name ? product.description : '-'}</span>
                                        </td>
                                        <td className="px-4 py-2.5 text-center xl:px-5">
                                            <div className="inline-flex min-w-[102px] items-center justify-center rounded-2xl border border-emerald-100 bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700 lg:min-w-[92px]">
                                                {formatPrice(product.price)}
                                            </div>
                                        </td>
                                        <td className="px-4 py-2.5 text-center xl:px-5">
                                            <span className={`inline-flex min-w-[86px] justify-center rounded-full px-2.5 py-1 text-xs font-semibold ${isOutOfStock
                                                ? 'bg-red-100 text-red-800'
                                                : product.stock < 10
                                                    ? 'bg-yellow-100 text-yellow-800'
                                                    : 'bg-green-100 text-green-800'
                                                }`}>
                                                {isOutOfStock ? 'Out' : product.stock} {!isOutOfStock && 'units'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2.5 text-center xl:px-5">
                                            <span className={`inline-flex min-w-[86px] justify-center rounded-full px-2.5 py-1 text-xs font-semibold ${!hasWarehouseStock
                                                ? 'bg-red-100 text-red-800'
                                                : isLowWarehouseStock
                                                    ? 'bg-orange-100 text-orange-800'
                                                    : 'bg-green-100 text-green-800'
                                                }`}>
                                                {warehouseStock} units
                                            </span>
                                        </td>
                                        <td className="px-4 py-2.5 text-center xl:px-5">
                                            {!hasWarehouseStock ? (
                                                <span className='inline-flex min-w-[112px] items-center justify-center gap-1 rounded-full bg-red-50 px-2.5 py-1 text-[11px] font-semibold text-red-700'>
                                                    <XCircle className="h-3.5 w-3.5" />
                                                    Out of Stock
                                                </span>
                                            ) : isLowWarehouseStock ? (
                                                <span className='inline-flex min-w-[112px] items-center justify-center gap-1 rounded-full bg-orange-50 px-2.5 py-1 text-[11px] font-semibold text-orange-700'>
                                                    <TriangleAlert className="h-3.5 w-3.5" />
                                                    Low Stock
                                                </span>
                                            ) : (
                                                <span className='inline-flex min-w-[112px] items-center justify-center gap-1 rounded-full bg-green-50 px-2.5 py-1 text-[11px] font-semibold text-green-700'>
                                                    <CheckCircle2 className="h-3.5 w-3.5" />
                                                    Available
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination - Only show when no error and data exists */}
            {!error && filteredProducts.length > 0 && (
                <div className="flex items-center justify-between gap-3 overflow-x-auto pb-10 text-xs whitespace-nowrap">
                    <div className="shrink-0 text-gray-500">
                        Showing {filteredProducts.length === 0 ? 0 : (currentPage - 1) * productsPerPage + 1}
                        -{Math.min(currentPage * productsPerPage, filteredProducts.length)} of {filteredProducts.length} items
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                        <button
                            type="button"
                            className="px-3 py-1.5 rounded-lg border text-xs disabled:opacity-50"
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                        >
                            Prev
                        </button>
                        <div className="text-xs text-gray-600">
                            Page {currentPage} of {totalPages}
                        </div>
                        <button
                            type="button"
                            className="px-3 py-1.5 rounded-lg border text-xs disabled:opacity-50"
                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}

            <ProductImagePreviewModal
                open={Boolean(previewProduct)}
                onClose={() => setPreviewProduct(null)}
                product={previewProduct}
            />
        </div>
    );
}

export default POSProductsTable;
